import React, { useEffect } from 'react';
import { getFramework, useIdentity } from '@/framework';
import { getLastMousePoint, setLastMousePoint, TouchKit } from '@/utils/event-kit';
import dragLayerPluginState, { getDragLayerState, type IDragGhostState } from './states/drag-state';
import './drag-layer.less';
import { IExportPlugin, PluginType } from '@/version-types';
import { DragLayerPlugin } from './drag-layer';

const ASIDE_SELECTOR = '.p-editor__aside';

const createEmptyDragImage = () => {
    const image = new Image();
    image.style.width = '1px';
    image.style.height = '1px';
    image.style.visibility = 'hidden';
    document.body.appendChild(image);
    return image;
};

const toNumber = (value: string | undefined) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const getGhostLimit = (ghost: HTMLElement) => {
    return {
        maxWidth: toNumber(ghost.dataset.maxWidth),
        maxHeight: toNumber(ghost.dataset.maxHeight),
    };
};

const getDragEndX = () => {
    return document.querySelector(ASIDE_SELECTOR)?.getBoundingClientRect().right ?? 0;
};

const applyGhostStyle = (ghost: HTMLElement, left: number, top: number, width?: number, height?: number) => {
    if (width && height) {
        ghost.style.width = `${width}px`;
        ghost.style.height = `${height}px`;
    }
    ghost.style.left = `${left}px`;
    ghost.style.top = `${top}px`;
};

const createDragGhostState = (event: DragEvent, ghost: HTMLElement | null): IDragGhostState | null => {
    if (!ghost) return null;

    const rect = ghost.getBoundingClientRect();
    const { maxWidth, maxHeight } = getGhostLimit(ghost);

    if (!rect.width || !rect.height || !maxWidth || !maxHeight) return null;

    return {
        defaultWidth: rect.width,
        defaultHeight: rect.height,
        maxWidth,
        maxHeight,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        startX: event.clientX,
        endX: getDragEndX(),
    };
};

const moveGhost = (event: DragEvent, ghost: HTMLElement, dragGhost: IDragGhostState) => {
    const { defaultWidth, defaultHeight, maxWidth, maxHeight, offsetX, offsetY, startX, endX } = dragGhost;

    if (event.clientX <= startX || endX <= startX || maxWidth === defaultWidth || maxHeight === defaultHeight) {
        applyGhostStyle(ghost, event.clientX - offsetX, event.clientY - offsetY);
        return;
    }

    if (event.clientX >= endX) {
        const rate = maxWidth / defaultWidth;
        applyGhostStyle(
            ghost,
            event.clientX - (defaultWidth / 2) * rate,
            event.clientY - (defaultHeight / 2) * rate,
            maxWidth,
            maxHeight,
        );
        return;
    }

    const slope = (maxWidth - defaultWidth) / (endX - startX);
    const width = defaultWidth + (event.clientX - startX) * slope;
    const height = (defaultHeight / defaultWidth) * width;
    const rate = width / defaultWidth;
    const slopeToCenterX = (defaultWidth / 2 - offsetX) / (maxWidth - defaultWidth);
    const slopeToCenterY = (defaultHeight / 2 - offsetY) / (maxHeight - defaultHeight);
    const x = (width - defaultWidth) * slopeToCenterX + offsetX;
    const y = (height - defaultHeight) * slopeToCenterY + offsetY;

    applyGhostStyle(ghost, event.clientX - x * rate, event.clientY - y * rate, width, height);
};

const resetDragState = (identity: string) => {
    const state = getDragLayerState(identity);
    state.ghost?.remove();
    state.reset();
};

const DragLayerPluginView: React.FC = React.memo(() => {
    const identity = useIdentity();
    const framework = getFramework(identity);

    // 自定义素材列表拖拽预览
    useEffect(() => {
        const image = createEmptyDragImage();

        const onDragStart = (event: DragEvent) => {
            const { ghost } = getDragLayerState(identity);
            const dragGhost = createDragGhostState(event, ghost);
            if (!dragGhost) return;

            getDragLayerState(identity).setDragGhost(dragGhost);

            if (event.dataTransfer) {
                // 隐藏原生拖拽预览
                event.dataTransfer.setDragImage(image, 0, 0);
                event.dataTransfer.effectAllowed = 'move';
            }
        };

        const onDragOver = (event: DragEvent) => {
            event.preventDefault();
            const { ghost, dragGhost } = getDragLayerState(identity);
            if (ghost && dragGhost) moveGhost(event, ghost, dragGhost);
        };

        const onDragEnd = () => {
            resetDragState(identity);
        };

        document.addEventListener('dragstart', onDragStart, true);
        document.addEventListener('dragover', onDragOver);
        document.addEventListener('dragend', onDragEnd);

        return () => {
            image.remove();
            document.removeEventListener('dragstart', onDragStart, true);
            document.removeEventListener('dragover', onDragOver);
            document.removeEventListener('dragend', onDragEnd);
        };
    }, [identity]);

    useEffect(() => {
        // 监听舞台 drop 事件，处理拖拽素材放置
        const handler = framework.service.editor.onDropLayer((e) => {
            const { dragData } = getDragLayerState(identity);

            e.preventDefault();
            e.stopPropagation();
            setLastMousePoint(e.clientX, e.clientY);
            const lastMousePoint = getLastMousePoint();

            const currentStageIndex = framework.service.transform.getFrameIndexByMousePointY(lastMousePoint.y);
            const stageIndex = framework.service.frame.getActiveIndex();
            const isInStage = framework.service.transform.checkPointInFrame(lastMousePoint, currentStageIndex);

            if (!isInStage) {
                resetDragState(identity);
                return;
            }

            if (currentStageIndex !== stageIndex) {
                framework.service.frame.setActiveIndex(currentStageIndex);
            }

            // 拖拽用户文件到舞台
            if (!dragData) {
                if (e?.dataTransfer?.files) {
                    framework.service.registration
                        .getInstance<DragLayerPlugin>('DragLayerPlugin')
                        .emitUploadFile(e?.dataTransfer?.files, lastMousePoint);
                }
                resetDragState(identity);
                return;
            }

            // 拖拽列表素材到舞台
            dragData.callback?.();

            // 拖动完成，触发一次全局的mouseup
            TouchKit.fireMouseup();
            resetDragState(identity);
        });

        return () => {
            framework.service.events.off(handler);
        };
    }, [framework, identity]);

    return null;
});

const exportPlugin: IExportPlugin = {
    type: PluginType.SYSTEM,
    sort: 3,
    name: 'drag-layer-plugin',
    render() {
        return <DragLayerPluginView />;
    },
    createInstance({ identity }) {
        return [{ key: 'DragLayerPlugin', value: new DragLayerPlugin(identity) }];
    },
    createState() {
        return [dragLayerPluginState];
    },
    destroy({ getInstance }) {
        getInstance<DragLayerPlugin>('DragLayerPlugin').destroy();
    },
};

export default exportPlugin;
